import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {AnswerFolderModel, ApiAnswerFolder} from '../../../models/api/answerFolderModel';
import {postApiAsync, putApiAsync} from '../../../services/http/http';
import {HttpResponseError} from '../../../services/http/httpError';
import {findAnswerFolderLock} from '../../../services/locks/resourceLocks';
import {ThunkAsyncAction} from '../../appThunk';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {getCompany} from '../company/companySelectors';
import {getOriginalData} from '../dataSelectors';
import {DataState} from '../dataState';
import {AnswerFolderNameAlreadyExistsError, updateAnswerFolderAction} from './answerFolderActions';

export interface AnswerFolderTemplate {
  name: string;
  namespace: string;
  folderId?: number;
}
export function createAnswerFolderAsyncAction(
  template: AnswerFolderTemplate,
): ThunkAsyncAction<AnswerFolderModel> {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);

    const url = `${company.url}/canned_answer_folders`;
    const body = {
      name: template.name,
      namespace: template.namespace,
      parent_folder_id: template.folderId,
    };

    try {
      const answerFolder = await postApiAsync(ApiAnswerFolder, url, body);
      dispatch(updateAnswerFolderAction(answerFolder));

      return answerFolder;
    } catch (error) {
      if (!(error instanceof HttpResponseError)) {
        throw error;
      }

      switch (error.status) {
        case 409:
          throw new AnswerFolderNameAlreadyExistsError();
        default:
          throw error;
      }
    }
  };
}

export function saveAnswerFolderAsyncAction(
  answerFolderId: number,
  template: AnswerFolderTemplate,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'answerFolders'>(
      'answerFolders',
      createObjectPropertyUpdater(
        'byId',
        createResourceMapValueUpdater(
          answerFolderId,
          createUpdater<AnswerFolderModel>((answerFolder) => ({
            ...answerFolder,
            name: template.name,
            namespace: template.namespace,
            parentFolderId: template.folderId,
          })),
        ),
      ),
    );

    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findAnswerFolderLock(answerFolderId).lockAsync(async () => {
          const state = getState();
          const {answerFolders} = getOriginalData(state);
          const answerFolder = answerFolders.byId[answerFolderId];

          if (!answerFolder) {
            return undefined;
          }

          try {
            const body = {
              name: template.name,
              namespace: template.namespace,
              parent_folder_id: template.folderId,
            };
            const newAnswerFolder = await putApiAsync(ApiAnswerFolder, answerFolder.url, body);

            return updateAnswerFolderAction(newAnswerFolder);
          } catch (error) {
            if (!(error instanceof HttpResponseError)) {
              throw error;
            }

            switch (error.status) {
              case 409:
                throw new AnswerFolderNameAlreadyExistsError();
              default:
                throw error;
            }
          }
        }),
      ),
    );
  };
}
